import { useRef, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Input, Logo } from '@nsibi/ui-web';
import { useTranslation } from 'react-i18next';

import { dobFromParts } from '../lib/emailAuth';
import { saveDisplayName, settle, submitDateOfBirth } from '../lib/onboarding';
import type { Session } from '../lib/session';
import { AgeRefused } from './AgeRefused';

const NAME_MIN = 2;
const NAME_MAX = 255;

/**
 * What a brand-new account owes before the app opens: a name, and a date of
 * birth when the server has none.
 *
 * Shown by the app WHILE the stored session says something is owed
 * (`Session.onboarding`), so a tab closed half way through comes back here
 * rather than in a chat list under no name. It is the phone's signup form
 * (`app/(auth)/profile.tsx`) with the same order of operations:
 *
 *  1. The DATE goes first, before a character of the profile is written. It is
 *     the only answer on this form that can end the account, and a refusal
 *     signs the session out - saving the name first would write a profile onto
 *     an account that is about to stop existing.
 *  2. Then the name. If that fails the date is NOT asked for again (the
 *     server takes it once; a second would be a 409) - the form simply stops
 *     offering it.
 *
 * The date boxes start EMPTY and say nothing about why they are asked: a
 * pre-filled year, or an explanation of the consequence, is a hint about which
 * answer gets you through.
 */
export function ProfileSetup({
  session,
  onDone,
  onRefused,
}: {
  session: Session;
  onDone: (s: Session) => void;
  /** The date was refused: drop the half-made session. The caller must NOT
   *  re-render this away - the notice has to stay on screen. */
  onRefused: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const owed = session.onboarding ?? { name: false, dob: false };
  const [asksName] = useState(owed.name);
  const [dobOwed, setDobOwed] = useState(owed.dob);
  const [name, setName] = useState(session.user.display_name ?? '');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  // What has been settled so far, so a retry starts from the right session.
  const [current, setCurrent] = useState(session);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const dob = dobFromParts(day, month, year);
  const trimmed = name.trim();
  const ready = (!asksName || trimmed.length >= NAME_MIN) && (!dobOwed || dob !== null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (asksName && trimmed.length < NAME_MIN) {
      setError(t('auth.web.pickAName', { defaultValue: 'Pick a name people will recognise.' }));
      return;
    }
    if (dobOwed && !dob) {
      setError(t('age.enterYours', { defaultValue: 'Enter your date of birth.' }));
      return;
    }

    setBusy(true);
    try {
      let working = current;
      if (dobOwed && dob) {
        const outcome = await submitDateOfBirth(dob);
        if (!outcome.ok) {
          if (outcome.refused) {
            setRefused(true);
            await onRefused();
          } else {
            setError(
              t('auth.somethingWentWrongTry', { defaultValue: 'Something went wrong. Try again.' }),
            );
          }
          return;
        }
        // Accepted, and the server takes it exactly once: stop offering it.
        setDobOwed(false);
        working = settle(working, { dob: true });
        setCurrent(working);
      }

      if (asksName) {
        try {
          const saved = await saveDisplayName(trimmed);
          working = settle(working, { name: true }, saved);
        } catch {
          setError(
            t('auth.couldnTSaveYourName', { defaultValue: "Couldn't save your name." }),
          );
          return;
        }
      }
      onDone(working);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-muted/40 px-6 py-12">
      <div className="w-full max-w-md">
        <Logo />
        <div className="mt-8">
          {refused ? (
            <AgeRefused />
          ) : (
            <form onSubmit={submit} noValidate>
              <h1 className="text-display">
                {asksName
                  ? t('auth.setUpYourProfile', { defaultValue: 'Set up your profile' })
                  : t('auth.whenSYourBirthday', { defaultValue: "When's your birthday?" })}
              </h1>
              {asksName && (
                <p className="mt-3 text-muted-foreground">
                  {t('auth.web.nameIsWhatContactsSee', {
                    defaultValue: 'Your name is what people see next to your messages and posts.',
                  })}
                </p>
              )}

              {asksName && (
                <>
                  <label htmlFor="display-name" className="mt-8 block text-sm font-medium">
                    {t('auth.yourName', { defaultValue: 'Your name' })}
                  </label>
                  <Input
                    id="display-name"
                    autoComplete="name"
                    autoFocus
                    maxLength={NAME_MAX}
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (error) setError(null);
                    }}
                    className="mt-2 h-11"
                  />
                </>
              )}

              {dobOwed && (
                <fieldset className="mt-8">
                  <legend className="text-sm font-medium">
                    {t('age.confirmTitle', { defaultValue: 'Your date of birth' })}
                  </legend>
                  <div className="mt-2 flex gap-3">
                    <Input
                      aria-label={t('age.dd', { defaultValue: 'DD' })}
                      placeholder={t('age.dd', { defaultValue: 'DD' })}
                      inputMode="numeric"
                      autoComplete="bday-day"
                      autoFocus={!asksName}
                      maxLength={2}
                      value={day}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                        setDay(v);
                        if (v.length === 2) monthRef.current?.focus();
                        if (error) setError(null);
                      }}
                      className="h-11 w-20 text-center"
                    />
                    <Input
                      ref={monthRef}
                      aria-label={t('age.mm', { defaultValue: 'MM' })}
                      placeholder={t('age.mm', { defaultValue: 'MM' })}
                      inputMode="numeric"
                      autoComplete="bday-month"
                      maxLength={2}
                      value={month}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                        setMonth(v);
                        if (v.length === 2) yearRef.current?.focus();
                        if (error) setError(null);
                      }}
                      className="h-11 w-20 text-center"
                    />
                    <Input
                      ref={yearRef}
                      aria-label={t('age.yyyy', { defaultValue: 'YYYY' })}
                      placeholder={t('age.yyyy', { defaultValue: 'YYYY' })}
                      inputMode="numeric"
                      autoComplete="bday-year"
                      maxLength={4}
                      value={year}
                      onChange={(e) => {
                        setYear(e.target.value.replace(/\D/g, '').slice(0, 4));
                        if (error) setError(null);
                      }}
                      className="h-11 w-28 text-center"
                    />
                  </div>
                </fieldset>
              )}

              {error && (
                <p role="alert" className="mt-4 text-sm text-destructive">
                  {error}
                </p>
              )}

              <Button type="submit" size="lg" disabled={busy || !ready} className="mt-6 w-full">
                {busy ? <Loader2 className="animate-spin" /> : null}
                {t('common.continue', { defaultValue: 'Continue' })}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
